import { useEffect, useRef, useState } from 'react';

import { useFileServerUpload } from '../use-file-server-upload';

export type UploadedFile = {
  id?: number | null;
  authToken?: string | null;
  url?: string | null;
  fileName: string;
  fileType: string;
  fileSize: number;
  progress: number;
  onCancel: (msg?: string) => void;
};

type Props = {
  onUploadStatusUpdate: (isUploading: boolean) => void;
};

export type UseUploadResult = {
  handleDeleteUpload: (upload: UploadedFile) => Promise<void>;
  handleUploadFile: (file: File) => Promise<void>;
  uploads: UploadedFile[];
};

export const useUpload = ({ onUploadStatusUpdate }: Props): UseUploadResult => {
  const [uploads, setUploads] = useState<UploadedFile[]>([]);
  const { deleteFile, uploadFile } = useFileServerUpload();
  const isMountedRef = useRef<boolean>(false);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleDeleteUpload = async ({
    id,
    authToken,
    progress,
    onCancel,
  }: UploadedFile): Promise<void> => {
    if (progress !== 100) onCancel();

    await deleteFile({ id, authToken });

    return setUploads(uploads.filter((upload) => id !== upload.id));
  };

  // oxlint-disable-next-line typescript/no-explicit-any
  const handleUploadFile = async (file: any): Promise<void> => {
    const abortController = new AbortController();
    let requestedUploadId: UploadedFile['id'];

    try {
      const {
        upload: { id },
        status,
      } = await uploadFile(file, {
        signal: abortController.signal,
        onUploadRequested: ({ id, authToken, url }) => {
          requestedUploadId = id;
          const onCancel = (): void => {
            abortController.abort();
            onUploadStatusUpdate(
              uploads.some(({ progress }) => progress !== 100)
            );
          };

          setUploads((prevUploads) => [
            ...prevUploads,
            {
              id,
              authToken,
              url,
              fileName: file.name,
              fileType: file.type,
              fileSize: file.size,
              onCancel,
              progress: 0,
            },
          ]);

          onUploadStatusUpdate(true);
        },
        onUploadProgress:
          // FIXME: This is a workaround because msw doesn't properly mock the
          // request object and without this it fails with:
          // "request.upload.addEventListener in not a function"
          typeof jest !== 'undefined'
            ? undefined
            : (progress, { id }) => {
                setUploads((prevUploads) =>
                  prevUploads.map((upload) =>
                    upload.id === id ? { ...upload, progress } : upload
                  )
                );

                onUploadStatusUpdate(progress !== 100);
              },
      });

      if (status === 204) {
        onUploadStatusUpdate(uploads.some(({ progress }) => progress !== 100));
        if (isMountedRef.current) {
          setUploads((prevUploads) =>
            prevUploads.map((upload) =>
              upload.id === id ? { ...upload, progress: 100 } : upload
            )
          );
        }
        return;
      }
    } catch (error) {
      if (requestedUploadId !== undefined) {
        setUploads(uploads.filter((upload) => requestedUploadId !== upload.id));
      }
      throw error;
    }

    throw new Error('Error uploading file');
  };

  return { handleUploadFile, handleDeleteUpload, uploads };
};
